import React, { useState, useEffect, useContext } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useStore } from "../../../stores/store";
import { useSetMenu } from "../../../hooks/useSetMenu";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import AuthContext from "../../../context/AuthProvider";
import { Card, Table, Badge, Form, Row, Col } from "react-bootstrap";
import { CircularProgress } from '@mui/material';
import "./ExamPlan.css";

const Index = () => {
    const { t } = useTranslation();
    const { auth } = useContext(AuthContext);
    const { enqueueSnackbar } = useSnackbar();
    const axiosPrivate = useAxiosPrivate();
    const { mainStore } = useStore();

    useSetMenu(mainStore.ExamPlanMenuType, mainStore.ExamPlanMenuNo);

    const [loading, setLoading] = useState(false);
    const [examPlans, setExamPlans] = useState([]);
    const [academicYears, setAcademicYears] = useState([]);
    const [termRegistrations, setTermRegistrations] = useState([]);
    const [selectedAcademicYear, setSelectedAcademicYear] = useState("");
    const [selectedTermRegistration, setSelectedTermRegistration] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        loadAcademicYears();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (selectedAcademicYear) {
            loadTermRegistrations(selectedAcademicYear);
        } else {
            setTermRegistrations([]);
            setSelectedTermRegistration("");
        }
        // eslint-disable-next-line
    }, [selectedAcademicYear]);

    useEffect(() => {
        loadExamPlans();
        // eslint-disable-next-line
    }, [selectedAcademicYear, selectedTermRegistration]);

    const loadAcademicYears = async () => {
        try {
            const response = await axiosPrivate.get(
                `/StaffPortalExamPlans/GetAcademicYearsWithExamPlans/${auth.universityId}`
            );
            setAcademicYears(response.data);
            if (response.data && response.data.length > 0) {
                setSelectedAcademicYear(response.data[0].value);
            }
        } catch (error) {
            console.error("Error loading academic years:", error);
            enqueueSnackbar(t("errorLoadingData"), { variant: "error" });
        }
    };

    const loadTermRegistrations = async (academicYearId) => {
        try {
            const response = await axiosPrivate.get(
                `/StaffPortalExamPlans/MyTermRegistrations?academicYearId=${academicYearId}`
            );
            setTermRegistrations(response.data);
            if (response.data && response.data.length > 0) {
                setSelectedTermRegistration(response.data[0].value);
            } else {
                setSelectedTermRegistration("");
            }
        } catch (error) {
            console.error("Error loading term registrations:", error);
            enqueueSnackbar(t("errorLoadingData"), { variant: "error" });
        }
    };

    const loadExamPlans = async () => {
        setLoading(true);
        try {
            let url = `/StaffPortalExamPlans/MyExamPlans`;
            const params = [];

            if (selectedAcademicYear) {
                params.push(`academicYearId=${selectedAcademicYear}`);
            }
            if (selectedTermRegistration) {
                params.push(`termRegistrationId=${selectedTermRegistration}`);
            }

            if (params.length > 0) {
                url += `?${params.join("&")}`;
            }

            const response = await axiosPrivate.get(url);
            setExamPlans(response.data);
        } catch (error) {
            console.error("Error loading exam plans:", error);
            enqueueSnackbar(t("errorLoadingData"), { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    const filteredExamPlans = examPlans.filter((plan) => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            (plan.coursesName && plan.coursesName.toLowerCase().includes(search)) ||
            (plan.facultyName && plan.facultyName.toLowerCase().includes(search)) ||
            (plan.levelName && plan.levelName.toLowerCase().includes(search)) ||
            (plan.branchObjectName && plan.branchObjectName.toLowerCase().includes(search))
        );
    });

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
        });
    };

    const getDayOfWeek = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const days = {
            lng1: ["E Diel", "E Hënë", "E Martë", "E Mërkurë", "E Enjte", "E Premte", "E Shtunë"],
            lng2: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
            lng3: ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"]
        };
        const lang = mainStore.lang || "lng2";
        return days[lang] ? days[lang][date.getDay()] : days.lng2[date.getDay()];
    };

    const getExamStatus = (dateString) => {
        if (!dateString) return "unknown";
        const examDate = new Date(dateString);
        const today = new Date();
        examDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        if (examDate.getTime() === today.getTime()) return "today";
        if (examDate > today) return "upcoming";
        return "past";
    };

    return (
        <div className="exam-plan-container">
            {}
            <Card className="mb-4 border-0 shadow-sm">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-1 text-brand">
                                <i className="bi bi-calendar-event me-2"></i>
                                {t("myExamPlan")}
                            </h4>
                            <p className="text-muted mb-0">{t("myExamPlanDescription")}</p>
                        </div>
                        <Badge bg="" className="bg-brand px-3 py-2">
                            {filteredExamPlans.length} {t("exams")}
                        </Badge>
                    </div>
                </Card.Body>
            </Card>

            {}
            <Card className="mb-4 border-0 shadow-sm">
                <Card.Body>
                    <h6 className="text-uppercase text-brand mb-3">
                        <i className="bi bi-funnel me-2"></i>
                        {t("filterOptions")}
                    </h6>
                    <Row>
                        <Col md={4} className="mb-3">
                            <Form.Group>
                                <Form.Label className="small text-muted">{t("academicYear")}</Form.Label>
                                <Form.Select
                                    value={selectedAcademicYear}
                                    onChange={(e) => setSelectedAcademicYear(e.target.value)}
                                    className="form-select-sm"
                                >
                                    <option value="">{t("selectAcademicYear")}</option>
                                    {academicYears.map((ay) => (
                                        <option key={ay.value} value={ay.value}>
                                            {ay.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4} className="mb-3">
                            <Form.Group>
                                <Form.Label className="small text-muted">{t("termRegistration")}</Form.Label>
                                <Form.Select
                                    value={selectedTermRegistration}
                                    onChange={(e) => setSelectedTermRegistration(e.target.value)}
                                    className="form-select-sm"
                                    disabled={!selectedAcademicYear}
                                >
                                    <option value="">{t("selectTermRegistration")}</option>
                                    {termRegistrations.map((tr) => (
                                        <option key={tr.value} value={tr.value}>
                                            {tr.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4} className="mb-3">
                            <Form.Group>
                                <Form.Label className="small text-muted">{t("searchPlaceholder")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder={t("searchPlaceholder")}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="form-control-sm"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {}
            <Card className="border-0 shadow-sm">
                <Card.Body>
                    <h6 className="text-uppercase text-brand mb-3">
                        <i className="bi bi-table me-2"></i>
                        {t("examPlanList")}
                    </h6>

                    {loading ? (
                        <div className="text-center py-5">
                            <CircularProgress sx={{ color: 'var(--brand-red, #dc354d)' }} />
                            <p className="mt-2 text-muted">{t("loading")}</p>
                        </div>
                    ) : filteredExamPlans.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="bi bi-calendar-x text-muted" style={{ fontSize: "3rem" }}></i>
                            <p className="mt-3 text-muted">{t("noExamPlansFound")}</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover className="exam-plan-table">
                                <thead className="bg-light">
                                    <tr>
                                        <th>{t("examDate")}</th>
                                        <th>{t("courses")}</th>
                                        <th>{t("faculty")} / {t("level")}</th>
                                        <th>{t("timeAndLocation")}</th>
                                        <th>{t("status")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredExamPlans.map((plan, index) => {
                                        const status = getExamStatus(plan.examDate);
                                        return (
                                            <tr key={`${plan.examPlanDetailId}-${index}`} className={`exam-row-${status}`}>
                                                {}
                                                <td>
                                                    <div className="date-cell">
                                                        <span className="fw-bold">{formatDate(plan.examDate)}</span>
                                                        <small className="text-muted d-block">{getDayOfWeek(plan.examDate)}</small>
                                                    </div>
                                                </td>
                                                {}
                                                <td>
                                                    <div className="courses-cell">
                                                        {plan.courses && plan.courses.length > 0 ? (
                                                            plan.courses.map((course, i) => (
                                                                <Badge
                                                                    key={i}
                                                                    bg="light"
                                                                    text="dark"
                                                                    className="me-1 mb-1 course-badge"
                                                                >
                                                                    {course.subjectCode} - {course.subjectName}
                                                                </Badge>
                                                            ))
                                                        ) : (
                                                            <span className="text-muted">{plan.coursesName || "-"}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                {}
                                                <td>
                                                    <div className="faculty-level-cell">
                                                        <span className="d-block">{plan.facultyName || "-"}</span>
                                                        <small className="text-muted">{plan.levelName || "-"}</small>
                                                    </div>
                                                </td>
                                                {}
                                                <td>
                                                    <div className="time-location-cell">
                                                        <Badge bg="" className="bg-brand time-badge mb-1">
                                                            <i className="bi bi-clock me-1"></i>
                                                            {plan.startTime || "-"}
                                                        </Badge>
                                                        <div className="location-info">
                                                            <small className="text-muted d-block">{plan.universityBranchName}</small>
                                                            <span className="d-block">{plan.branchObjectName || "-"}</span>
                                                        </div>
                                                        <div className="rooms-info mt-1">
                                                            {plan.rooms && plan.rooms.length > 0 ? (
                                                                plan.rooms.map((room, i) => (
                                                                    <Badge
                                                                        key={i}
                                                                        bg={room.roomCode === "0" ? "info" : "secondary"}
                                                                        className={`me-1 ${room.roomCode === "0" ? "online-badge" : "room-badge"}`}
                                                                    >
                                                                        <i className={`bi ${room.roomCode === "0" ? "bi-wifi" : "bi-door-open"} me-1`}></i>
                                                                        {room.roomCode === "0" ? t("online") : (room.roomCode || room.roomName)}
                                                                    </Badge>
                                                                ))
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </td>
                                                {}
                                                <td>
                                                    {status === "today" && (
                                                        <Badge bg="danger">
                                                            <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                                            {t("today")}
                                                        </Badge>
                                                    )}
                                                    {status === "upcoming" && (
                                                        <Badge bg="success">
                                                            <i className="bi bi-calendar-check me-1"></i>
                                                            {t("upcoming")}
                                                        </Badge>
                                                    )}
                                                    {status === "past" && (
                                                        <Badge bg="secondary">
                                                            <i className="bi bi-check-circle me-1"></i>
                                                            {t("completed")}
                                                        </Badge>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {}
            {filteredExamPlans.some(plan => plan.specialInstruction) && (
                <Card className="mt-4 border-0 shadow-sm">
                    <Card.Body>
                        <h6 className="text-uppercase text-brand mb-3">
                            <i className="bi bi-info-circle me-2"></i>
                            {t("specialInstructions")}
                        </h6>
                        {filteredExamPlans
                            .filter(plan => plan.specialInstruction)
                            .map((plan, index) => (
                                <div key={index} className="instruction-item mb-2 p-2 bg-light rounded">
                                    <strong>{plan.coursesName}:</strong>
                                    <p className="mb-0 text-muted">{plan.specialInstruction}</p>
                                </div>
                            ))
                        }
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};

export default observer(Index);
